import React, { useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { tokenManager } from "../utils/api";
import { authService } from "../services/authService";
import { jwtDecode } from "jwt-decode";
import urlImage from "../assets/images/auth-image.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    password: ""
  });

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    setError("");
    setFieldErrors({ email: "", password: "" });

    let hasErrors = false;
    const errors = { email: "", password: "" };

    if (!email.trim()) {
      errors.email = "Please enter your email address";
      hasErrors = true;
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.email = "Please enter a valid email address";
        hasErrors = true;
      }
    }

    if (!password.trim()) {
      errors.password = "Please enter your password";
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(errors);
      return;
    }

    try {
      const response = await authService.login(email, password);

      if (response && response.token) {
        tokenManager.setTokens(response.token, response.refreshToken);
        localStorage.setItem("userEmail", email);
        if (response.user) {
          localStorage.setItem("user", JSON.stringify(response.user));
        }

        const decoded = jwtDecode(response.token);
        const userRole = decoded.roles[0];

        if (userRole === "ADMIN" || userRole === "SUPERADMIN" || userRole === "CLIENT") {
          navigate("/analytics");
        } else if (userRole === "USER") {
          navigate("/urlShorten");
        }
      } else {
        setError(
          "Authentication failed. Please check your credentials and try again."
        );
      }
    } catch (err) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("user");

      if (err.response?.data?.message) {
        setError(err.response.data.message);

        if (
          err.response.data.message ===
          "Email is not verified. Please verify your email before logging in."
        ) {
          navigate("/auth/verify-email", { state: { email: email } });
          return;
        }
      } else if (!navigator.onLine) {
        setError(
          "No internet connection. Please check your network and try again."
        );
      } else {
        setError("An error occurred during login. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const navigate = useNavigate();

  return (
    <Container fluid className="vh-100">
      <Row className="h-100 justify-content-center">
        <Col lg={4} md={12} sm={12} className="d-none d-md-block">
          <div
            className="h-100 w-100"
            style={{
              backgroundImage: `url(${urlImage})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />
        </Col>
        <Col
          lg={6}
          md={12}
          sm={12}
          className="d-flex justify-content-center align-items-center"
        >
          <Card className="p-4 auth-form-card shadow br-radius-12">
            <div className="text-start mb-3">
              <Button
                variant="link"
                onClick={handleBack}
                className="text-dark p-0"
                style={{ textDecoration: "none" }}
              >
                ←
              </Button>
            </div>
            <div className="text-center mb-4">
              <h5 className="GeneralSansSemibold pt-3 fs-20">
                URL Shortening.
              </h5>
              <p className="mb-0 fs-14">Please log in to your account</p>
            </div>
            <Form onSubmit={handleSubmit} noValidate>
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <Form.Group controlId="formBasicEmail" className="mb-3">
                <Form.Label className="fs-12">Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: "" }));
                  }}
                  isInvalid={!!fieldErrors.email}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formBasicPassword" className="mb-3">
                <Form.Label className="fs-12">Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: "" }));
                    }}
                    isInvalid={!!fieldErrors.password}
                    required
                    className="pe-5"
                  />
                  <span
                    className="position-absolute top-50 end-3 right-3 translate-middle-y fs-12 text-success"
                    style={{ right: `${showPassword ? "10px" : "10px"}` }}
                    onClick={togglePasswordVisibility}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.password}
                  </Form.Control.Feedback>
                </div>
                <div className="text-end mt-1">
                  <a
                    href="/auth/forgot-password"
                    className="text-success fs-12 text-decoration-none"
                  >
                    Forgot Password?
                  </a>
                </div>
              </Form.Group>
              
                <Button variant="success" type="submit" className="w-100" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
              

              <p className="fs-12 mt-3 text-center">
                Don't have an account yet?{" "}
                <a className="text-dark" href="/auth/signup">
                  Sign up here
                </a>
              </p>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
